export interface PostalCodeValidationResult {
    isValid: boolean;
    errorMessage?: string;
}

interface PostalCodeRule {
    regex: RegExp;
    errorMessage: string;
    label: string;
    placeholder: string;
}

const POSTAL_CODE_RULES: Record<string, PostalCodeRule> = {
    US: {
        regex: /^[0-9]{5}(-[0-9]{4})?$/,
        errorMessage: 'Please enter a valid 5-digit ZIP code (e.g., 12345)',
        label: 'Billing Zip Code',
        placeholder: '12345',
    },
    AU: {
        regex: /^[0-9]{4}$/,
        errorMessage: 'Please enter a valid 4-digit postcode (e.g., 2000)',
        label: 'Postcode',
        placeholder: '2000',
    },
    // Default rule for other countries - more flexible
    DEFAULT: {
        regex: /^.{3,10}$/,
        errorMessage: 'Postal code should be between 3 and 10 characters',
        label: 'Postal Code',
        placeholder: '12345',
    },
};

/**
 * Validates a postal code based on the country
 * @param postalCode - The postal code to validate
 * @param countryCode - The ISO country code (e.g., 'US', 'AU')
 * @returns Validation result with isValid flag and optional error message
 */
export function validatePostalCode(postalCode: string, countryCode: string): PostalCodeValidationResult {
    if (!postalCode || postalCode.trim() === '') {
        return {
            isValid: false,
            errorMessage: 'Postal code is required.',
        };
    }

    const trimmedCode = postalCode.trim();
    const rule = POSTAL_CODE_RULES[countryCode] || POSTAL_CODE_RULES.DEFAULT;

    if (!rule.regex.test(trimmedCode)) {
        return {
            isValid: false,
            errorMessage: rule.errorMessage,
        };
    }

    return { isValid: true };
}

/**
 * Gets the appropriate label for postal code field based on country
 * @param countryCode - The ISO country code
 * @returns The label text (e.g., 'Billing Zip Code', 'Postcode')
 */
export function getPostalCodeLabel(countryCode: string): string {
    const rule = POSTAL_CODE_RULES[countryCode] || POSTAL_CODE_RULES.DEFAULT;
    return rule.label;
}

/**
 * Gets the appropriate placeholder for postal code field based on country
 * @param countryCode - The ISO country code
 * @returns The placeholder text
 */
export function getPostalCodePlaceholder(countryCode: string): string {
    const rule = POSTAL_CODE_RULES[countryCode] || POSTAL_CODE_RULES.DEFAULT;
    return rule.placeholder;
}

/**
 * Checks if we should use "Zip Code" terminology (US-specific)
 * @param countryCode - The ISO country code
 * @returns True if US or no country specified
 */
export function useZipCodeLabel(countryCode: string | undefined): boolean {
    return countryCode === 'US' || !countryCode;
}
